import isEmpty from 'lodash/isEmpty';
import { BlockType } from '../../../ArticleBodyRenderer/types';

import { createVirtualElementWithHtml } from '../../../../services/dom/createVirtualElementWithHtml';
import { getQuerySelectorForHighlightHint } from './getQuerySelectorForHighlightHint';
import { HighlightSelectionHint, THighlightBodyBlock, TListHighlightBlockData } from '../../types';

export const getHtmlOrTextFromElement = (element?: Element | null): string =>
  (element?.innerHTML || element?.textContent || '').trim();

export const getHighlightBlockDataFromHtml = (blockType: string, html: string): THighlightBodyBlock['data'] => {
  const element = createVirtualElementWithHtml(html);

  if (blockType === BlockType.Paragraph || blockType === BlockType.Header) {
    return {
      text: getHtmlOrTextFromElement(
        element.querySelector(getQuerySelectorForHighlightHint(HighlightSelectionHint.Text)),
      ),
    };
  }

  if (blockType === BlockType.Code) {
    return {
      code: getHtmlOrTextFromElement(
        element.querySelector(getQuerySelectorForHighlightHint(HighlightSelectionHint.Code)),
      ),
    };
  }

  if (blockType === BlockType.Quote) {
    return {
      text: getHtmlOrTextFromElement(
        element.querySelector(getQuerySelectorForHighlightHint(HighlightSelectionHint.Text)),
      ),
      caption: getHtmlOrTextFromElement(
        element.querySelector(getQuerySelectorForHighlightHint(HighlightSelectionHint.Caption)),
      ),
    };
  }

  if (blockType === BlockType.Image) {
    return {
      file: {
        url:
          element.querySelector(getQuerySelectorForHighlightHint(HighlightSelectionHint.Image))?.dataset.originalUrl ||
          '',
      },
      caption: getHtmlOrTextFromElement(
        element.querySelector(getQuerySelectorForHighlightHint(HighlightSelectionHint.Caption)),
      ),
    };
  }

  const handleListElement = (listElement: HTMLElement): TListHighlightBlockData | undefined => {
    const items = Array.from(
      listElement?.querySelectorAll(
        `:scope > ${getQuerySelectorForHighlightHint(HighlightSelectionHint.List)} > ${getQuerySelectorForHighlightHint(
          HighlightSelectionHint.ListElement,
        )}`,
      ) || [],
    )
      .map(handleListElement)
      .filter(Boolean);
    const content = getHtmlOrTextFromElement(
      listElement.querySelector(`:scope > ${getQuerySelectorForHighlightHint(HighlightSelectionHint.Text)}`),
    );

    if (!content && isEmpty(items)) {
      return undefined;
    }

    return {
      items,
      content,
    };
  };

  if (blockType === BlockType.List) {
    return {
      ...(handleListElement(element) || { items: [], content: '' }),
    };
  }

  if (blockType === BlockType.Table) {
    const content = Array.from(
      element.querySelectorAll(getQuerySelectorForHighlightHint(HighlightSelectionHint.TableRow)) || [],
    ).map((trElement) =>
      Array.from(
        trElement.querySelectorAll(getQuerySelectorForHighlightHint(HighlightSelectionHint.TableColumn)) || [],
      ).map(getHtmlOrTextFromElement),
    );

    return {
      content,
    };
  }

  return {};
};
